import { Component, computed, inject } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { CartService } from '../../services/cart.service';
import { CartLineView } from '../../models/cart.model';
import { toCartLineView } from '../../cart/cart.utils';
import { Item } from '../../models/menu.model';
import { ModifierComponent } from '../modifier/modifier.component';

export interface ItemVariantsSheetData {
  item: Item;
  subSectionId?: string;
  isOptionalSubSection?: boolean;
}

// Shown instead of a plain increase/decrease when an item with modifiers already has
// one or more distinct modifier combinations ("variants") in the cart - lets the user
// pick which one to adjust, or configure a new variant, rather than guessing which
// cart line a bare +/- tap should apply to.
@Component({
  selector: 'app-item-variants-sheet',
  templateUrl: './item-variants-sheet.component.html',
  styleUrl: './item-variants-sheet.component.scss',
  standalone: false,
})
export class ItemVariantsSheetComponent {
  public sheetRef = inject<MatBottomSheetRef<ItemVariantsSheetComponent>>(MatBottomSheetRef);
  public data = inject<ItemVariantsSheetData>(MAT_BOTTOM_SHEET_DATA);

  private cartService = inject(CartService);
  private bottomSheet = inject(MatBottomSheet);

  // Scoped to the opening card's subsection, matching the lines that card counts and decrements
  // - the same item listed in another section has its own, separate lines.
  public variants = computed<CartLineView[]>(() => this.cartService.cartLines()
    .filter(line => line.item.id === this.data.item.id && (line.subSectionId || '') === (this.data.subSectionId || ''))
    .map(toCartLineView));

  public increase(lineId: string): void {
    this.cartService.incrementLine(lineId);
  }

  public decrease(lineId: string): void {
    this.cartService.decrementLine(lineId);
  }

  public addNewVariant(): void {
    this.sheetRef.dismiss();
    this.bottomSheet.open(ModifierComponent, {
      autoFocus: false,
      data: {
        item: this.data.item,
        subSectionId: this.data.subSectionId,
        isOptionalSubSection: this.data.isOptionalSubSection,
      },
    });
  }

  public editVariant(lineId: string): void {
    const cartLine = this.cartService.cartLines().find(line => line.id === lineId);
    if (!cartLine) return;

    this.sheetRef.dismiss();
    this.bottomSheet.open(ModifierComponent, {
      autoFocus: false,
      data: {
        item: cartLine.item,
        subSectionId: cartLine.subSectionId,
        isOptionalSubSection: cartLine.isOptionalSubSection,
        cartLine,
      },
    });
  }

  public close(): void {
    this.sheetRef.dismiss();
  }
}
